import React, { useState, useEffect, useRef } from 'react';
import { VolumeX } from 'lucide-react';
import { createPortal } from 'react-dom';

const FloatingVoiceAI = ({ voiceAssist, toggleVoiceAssist, handleVoiceClick }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [edge, setEdge] = useState('right'); 
    const [yPos, setYPos] = useState(window.innerHeight * 0.65);
    const [dragPos, setDragPos] = useState(null); 
    const [isDragging, setIsDragging] = useState(false);
    
    // 🌟 100x Real Zoom Physics State
    const [appScale, setAppScale] = useState(1);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    
    const dragRef = useRef({ startX: 0, startY: 0, offsetX: 0, offsetY: 0, moved: false });
    const barsRef = useRef([]);

    // Window Resize & Zoom Level Sync
    useEffect(() => {
        const syncPhysics = () => {
            setWindowWidth(window.innerWidth);
            const currentScale = parseFloat(localStorage.getItem('appScale')) || 1.0;
            setAppScale(currentScale);
        };
        
        window.addEventListener('resize', syncPhysics);
        window.addEventListener('appThemeChanged', syncPhysics); // React to zoom changes instantly
        syncPhysics();
        
        return () => {
            window.removeEventListener('resize', syncPhysics);
            window.removeEventListener('appThemeChanged', syncPhysics);
        };
    }, []);

    // Animation: AI Voice Waveform
    useEffect(() => {
        if (!voiceAssist) {
            barsRef.current.forEach(bar => { if (bar) bar.style.height = '3px'; });
            return;
        }

        let isRunning = true;
        const animateWave = () => {
            if (!isRunning) return;
            
            const isSpeaking = window.speechSynthesis && window.speechSynthesis.speaking;
            barsRef.current.forEach((bar, i) => {
                if (!bar) return;
                if (isSpeaking) {
                    const maxH = i === 2 ? 22 : (i === 1 || i === 3 ? 16 : 10);
                    const minH = 4;
                    const newHeight = Math.floor(Math.random() * (maxH - minH + 1)) + minH;
                    bar.style.height = `${newHeight}px`;
                } else {
                    bar.style.height = '3px';
                }
            });
            setTimeout(animateWave, isSpeaking ? 70 : 200); 
        };
        animateWave();

        return () => {
            isRunning = false;
            barsRef.current.forEach(bar => { if (bar) bar.style.height = '3px'; });
        };
    }, [voiceAssist]);

    // Dragging Logic with Zoom Neutralization
    const handlePointerDown = (e) => {
        setIsDragging(true);
        const rect = e.currentTarget.getBoundingClientRect();
        dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            offsetX: e.clientX - rect.left * appScale, // 👈 Adjust for Zoom
            offsetY: e.clientY - rect.top * appScale,  // 👈 Adjust for Zoom
            moved: false
        };
        setDragPos({ x: rect.left, y: rect.top });
        e.currentTarget.setPointerCapture(e.pointerId);
    };

    const handlePointerMove = (e) => {
        if (!isDragging) return;
        const dx = e.clientX - dragRef.current.startX;
        const dy = e.clientY - dragRef.current.startY;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragRef.current.moved = true;
        
        const w = isOpen ? 125 : 38; 
        
        // 🌟 100x Real: Divide by appScale to neutralize app layout zoom
        let newX = (e.clientX - dragRef.current.offsetX) / appScale;
        let newY = (e.clientY - dragRef.current.offsetY) / appScale;
        
        // boundary checks relative to unscaled window
        const realWindowWidth = windowWidth / appScale;
        const realWindowHeight = window.innerHeight / appScale;

        // 10px गैप को 0 कर दिया गया है
        newX = Math.max(0, Math.min(newX, realWindowWidth - w));
        newY = Math.max(10, Math.min(newY, realWindowHeight - 38 - 10));
        
        setDragPos({ x: newX, y: newY });
    };

    const handlePointerUp = (e) => {
        if (!isDragging) return;
        setIsDragging(false);
        e.currentTarget.releasePointerCapture(e.pointerId);
        
        if (!dragRef.current.moved) {
            // Short Tap/Click -> Open Menu
            if (!isOpen) handleVoiceClick(e, "स्मार्ट वॉइस मेनू खोला गया है।", () => setIsOpen(true));
            setDragPos(null);
        } else {
            // Dropped -> Magnetic Snap Logic (Zero Lag)
            const w = isOpen ? 125 : 38;
            const currentX = dragPos ? dragPos.x : e.clientX / appScale;
            const realWindowWidth = windowWidth / appScale;
            const centerX = realWindowWidth / 2;
            
            const newEdge = (currentX + w / 2) < centerX ? 'left' : 'right';
            
            setEdge(newEdge);
            setYPos(Math.max(10, Math.min(dragPos ? dragPos.y : e.clientY / appScale, (window.innerHeight / appScale) - 38 - 10)));
            setDragPos(null); 
        }
    };

    // Magnetic Layout Calculations
    const w = isOpen ? 125 : 38;
    const isLeft = edge === 'left';
    
    // 🌟 Perfect Edge snapping regardless of Zoom Level
    const realWindowWidth = windowWidth / appScale;
    // 16px को 0 कर दिया गया है ताकि स्क्रीन से पूरा टच हो जाए
    const currentLeft = dragPos ? dragPos.x : (isLeft ? 0 : realWindowWidth - w);
    const currentTop = dragPos ? dragPos.y : yPos;

    return createPortal(
        <>
            <style>{`
                @keyframes aiGlowMini {
                    0% { box-shadow: 0 0 8px rgba(16, 185, 129, 0.4), inset 0 0 4px rgba(16, 185, 129, 0.2); }
                    50% { box-shadow: 0 0 20px rgba(16, 185, 129, 0.8), inset 0 0 10px rgba(16, 185, 129, 0.4); }
                    100% { box-shadow: 0 0 8px rgba(16, 185, 129, 0.4), inset 0 0 4px rgba(16, 185, 129, 0.2); }
                }
            `}</style>

            <div 
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                style={{
                    position: 'fixed',
                    margin: 0,
                    left: `${currentLeft}px`,
                    top: `${currentTop}px`,
                    zIndex: 2147483647,
                    touchAction: 'none',
                    display: 'flex',
                    flexDirection: isLeft ? 'row' : 'row-reverse',
                    alignItems: 'center',
                    background: voiceAssist ? 'rgba(10, 20, 15, 0.85)' : 'rgba(15, 15, 17, 0.85)', 
                    backdropFilter: 'blur(20px) saturate(150%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(150%)',
                    border: voiceAssist ? '1px solid rgba(16, 185, 129, 0.5)' : '1px solid rgba(255,255,255,0.15)',
                    boxShadow: isDragging ? '0 15px 40px rgba(0,0,0,0.5)' : (voiceAssist ? '0 8px 25px rgba(16, 185, 129, 0.3)' : '0 8px 20px rgba(0,0,0,0.3)'),
                    animation: voiceAssist && !isDragging ? 'aiGlowMini 2.5s infinite' : 'none',
                    borderRadius: '40px', 
                    padding: '4px', 
                    width: `${w}px`, 
                    height: '38px',
                    transition: isDragging ? 'none' : 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)', 
                    transform: isDragging ? 'scale(1.08)' : 'scale(1)',
                    overflow: 'hidden',
                    cursor: isDragging ? 'grabbing' : 'grab'
                }}
            >
                {/* Voice Icon Base */}
                <div
                    onClick={(e) => {
                        if(isOpen) {
                            e.stopPropagation();
                            handleVoiceClick(e, "मेनू बंद किया जा रहा है।", () => setIsOpen(false));
                        }
                    }}
                    style={{
                        minWidth: '30px', height: '30px', 
                        borderRadius: '50%',
                        background: voiceAssist ? 'linear-gradient(135deg, #10B981, #047857)' : 'rgba(255,255,255,0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px',
                        boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.2)',
                        transition: 'all 0.3s ease',
                        flexShrink: 0,
                        cursor: 'pointer'
                    }}
                >
                    {voiceAssist ? (
                        <>
                            {[0, 1, 2, 3, 4].map(i => (
                                <div 
                                    key={i} 
                                    ref={el => barsRef.current[i] = el} 
                                    style={{ width: '2.5px', height: '3px', background: '#fff', borderRadius: '2px', transition: 'height 0.08s ease-out' }} 
                                />
                            ))}
                        </>
                    ) : (
                        <VolumeX size={14} color="#ddd" />
                    )}
                </div>

                {/* Expanded Menu */}
                <div style={{
                    display: 'flex', 
                    flexDirection: isLeft ? 'row' : 'row-reverse', 
                    alignItems: 'center', justifyContent: 'space-between',
                    width: '100%', 
                    paddingLeft: isLeft ? '8px' : '4px', 
                    paddingRight: isLeft ? '4px' : '8px',
                    opacity: isOpen ? 1 : 0,
                    transform: isOpen ? 'translateX(0)' : `translateX(${isLeft ? '-15px' : '15px'})`,
                    transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    pointerEvents: isOpen ? 'auto' : 'none',
                    whiteSpace: 'nowrap'
                }}>
                    <span style={{ fontSize: '8px', fontWeight: '900', color: '#fff', letterSpacing: '0.5px' }}>
                        VOICE
                    </span>

                    {/* Toggle Switch */}
                    <div 
                        onPointerDown={(e) => e.stopPropagation()} 
                        onClick={(e) => {
                            e.stopPropagation();
                            handleVoiceClick(e, voiceAssist ? "वॉइस एआई बंद किया गया।" : "वॉइस एआई चालू किया गया।", toggleVoiceAssist);
                            setTimeout(() => setIsOpen(false), 700);
                        }}
                        style={{
                            width: '30px', height: '16px', 
                            borderRadius: '16px',
                            background: voiceAssist ? '#10B981' : 'rgba(255,255,255,0.2)',
                            position: 'relative',
                            transition: 'background 0.3s ease',
                            cursor: 'pointer',
                            boxShadow: 'inset 0 1px 4px rgba(0,0,0,0.5)',
                            marginLeft: isLeft ? '0' : '6px',
                            marginRight: isLeft ? '6px' : '0'
                        }}
                    >
                        <div style={{
                            width: voiceAssist ? '14px' : '12px', 
                            height: '12px',
                            borderRadius: '50%',
                            background: '#fff',
                            position: 'absolute',
                            top: '2px',
                            left: voiceAssist ? '14px' : '2px',
                            transition: 'all 0.3s cubic-bezier(0.5, 2, 0.5, 0.8)', 
                            boxShadow: '0 1px 4px rgba(0,0,0,0.4)'
                        }} />
                    </div>
                </div>
            </div>
        </>,
        document.body
    );

};

export default FloatingVoiceAI;